/**
 * Web-side wiring for Response Coverage & Response-Order Intelligence.
 *
 * Station data is the full statewide NSW RFS facilities seed (real open
 * data) — not gated by an incident's locality, and dispatch candidates are
 * pruned by proximity, never clipped to a district/LGA boundary. The seeded
 * road graph is now real OpenStreetMap data (ingested via Overpass) for the
 * Bungendore/QPRC corridor — but it's a large payload (~4 MB), so only its
 * small metadata sidecar (bounds/attribution/fetch URL) is imported here;
 * the actual node/edge graph is fetched by the worker on demand from
 * `web/public/`, never bundled into the JS build. Outside that corridor,
 * every station still gets an honest haversine fallback (lower confidence,
 * never "unavailable"). See docs/response-coverage-2.0.md.
 */
import nswStationSeed from '@data/stations/nsw-rfs-facilities.json';
import roadGraphMeta from '@data/roads/bungendore-qprc-osm-road-graph.meta.json';
import {
  haversineMeters,
  type BrigadeStation,
  type CoverageBounds,
  type Incident,
  type LngLat,
  type StationDataset,
  type TurnoutProfile,
} from '@duty/shared';

export interface StationSeedFile extends StationDataset {
  turnoutProfiles: Record<string, TurnoutProfile>;
}

export interface RoadGraphMeta {
  id: string;
  description: string;
  provenance: 'osm' | 'synthetic-seed';
  source: string;
  generatedAt: string;
  nodeCount: number;
  edgeCount: number;
  bounds: CoverageBounds;
  /** Fetch this at runtime (worker-side) to get the actual graph — never statically imported. */
  url: string;
}

const NSW_STATIONS = nswStationSeed as StationSeedFile;
const ROAD_GRAPH_META = roadGraphMeta as RoadGraphMeta;

/** Every seeded NSW RFS station — real open-data coordinates, statewide (1,558 in the current seed). */
export function allStations(): BrigadeStation[] {
  return NSW_STATIONS.stations;
}

export function stationAttribution(): string[] {
  return NSW_STATIONS.attribution;
}

/** Base (pre-override) turnout profiles for a set of stations, restricted to just those ids. */
export function turnoutProfilesFor(stations: readonly BrigadeStation[]): Record<string, TurnoutProfile> {
  const out: Record<string, TurnoutProfile> = {};
  for (const s of stations) {
    const p = NSW_STATIONS.turnoutProfiles[s.id];
    if (p) out[s.id] = p;
  }
  return out;
}

/** Metadata for the one seeded road graph available in the POC (Bungendore/QPRC corridor). */
export function defaultRoadGraphMeta(): RoadGraphMeta {
  return ROAD_GRAPH_META;
}

/** The URL the coverage worker fetches the actual (large) graph payload from. */
export function defaultRoadGraphUrl(): string {
  return ROAD_GRAPH_META.url;
}

/**
 * Response Coverage is available for any incident with a known point —
 * accuracy varies by proximity to the seeded graph, but the haversine
 * fallback means it never hard-fails to "unavailable" statewide.
 */
export function coverageAvailableFor(incident: Incident): boolean {
  return incident.coordinates !== undefined;
}

export interface CandidateOptions {
  maxRadiusM?: number;
  maxCount?: number;
}

/**
 * Nearest stations to a point, pruned by radius + count so a ranked list/grid
 * stay usable against a 1,500+ station statewide dataset (mirrors the
 * "cheap haversine bound" candidate-selection step in
 * docs/response-coverage-2.0.md §3.2 step 1). Used for a single-incident
 * lookup, where there's one clear reference point to rank distance from.
 */
export function candidateStationsNear(point: LngLat, opts: CandidateOptions = {}): BrigadeStation[] {
  const { maxRadiusM = 80_000, maxCount = 12 } = opts;
  return NSW_STATIONS.stations
    .map((s) => ({ station: s, distanceM: haversineMeters(point, s.coord) }))
    .filter((c) => c.distanceM <= maxRadiusM)
    .sort((a, b) => a.distanceM - b.distanceM)
    .slice(0, maxCount)
    .map((c) => c.station);
}

export interface ViewportCandidateOptions {
  /** Fraction of the viewport's width/height to extend on each side, so the edge of the visible
   * coverage grid is correctly attributed to stations whose territory only partly overlaps the
   * viewport, not clipped to whoever happens to be exactly on-screen. */
  bufferRatio?: number;
  /** A performance safety net only — real NSW-wide station counts (~1,558) should never come close
   * to this; it exists so an extreme zoom-out can't pathologically blow out the worker's per-request cost. */
  maxCount?: number;
  /** Floor for the buffer, in metres, regardless of zoom (see below) — default matches the ~60 min
   * horizon at the haversine-fallback's nominal speed/directness assumptions (response-order.ts). */
  minBufferMeters?: number;
}

const METERS_PER_DEGREE_LAT = 111_320;

/**
 * Every station whose coordinate falls within `bounds`, expanded by a buffer
 * on each side — the full-coverage, viewport-driven candidate set for the
 * no-incident planning tool (deliberately NOT capped to "N nearest to a
 * point": every station relevant to what's on screen (plus the buffer)
 * should be analysed, so panning/zooming reveals real neighbouring brigades
 * rather than an arbitrary top-12).
 *
 * The buffer is `max(bufferRatio * viewport span, minBufferMeters)` — a pure
 * percentage-of-viewport buffer is not enough on its own: at a normal
 * incident-scale zoom (a few km across), 50% of the viewport is only ~1-2 km,
 * nowhere near the ~55-80 km a genuinely competitive neighbouring brigade can
 * sit at once turn-out is factored in. Without a real-world floor, a closer
 * pan can silently drop that competitor from consideration and let a farther,
 * still-in-view station incorrectly "win" cells it shouldn't — which reads
 * as the coverage area visibly ballooning/contracting as the user pans, even
 * though nothing about the actual road network or drive times changed.
 */
export function candidateStationsInBounds(bounds: CoverageBounds, opts: ViewportCandidateOptions = {}): BrigadeStation[] {
  const { bufferRatio = 0.5, maxCount = 500, minBufferMeters = 60_000 } = opts;
  const lngSpan = bounds.maxLng - bounds.minLng;
  const latSpan = bounds.maxLat - bounds.minLat;
  const centerLat = (bounds.minLat + bounds.maxLat) / 2;
  const metersPerDegLng = Math.max(1, METERS_PER_DEGREE_LAT * Math.cos((centerLat * Math.PI) / 180));
  const latBuffer = Math.max(latSpan * bufferRatio, minBufferMeters / METERS_PER_DEGREE_LAT);
  const lngBuffer = Math.max(lngSpan * bufferRatio, minBufferMeters / metersPerDegLng);
  const padded: CoverageBounds = {
    minLng: bounds.minLng - lngBuffer,
    maxLng: bounds.maxLng + lngBuffer,
    minLat: bounds.minLat - latBuffer,
    maxLat: bounds.maxLat + latBuffer,
  };
  const within = NSW_STATIONS.stations.filter(
    (s) =>
      s.coord.lng >= padded.minLng &&
      s.coord.lng <= padded.maxLng &&
      s.coord.lat >= padded.minLat &&
      s.coord.lat <= padded.maxLat,
  );
  if (within.length <= maxCount) return within;
  // Only reachable at an extreme zoomed-out view (approaching all of NSW) — prefer the stations
  // nearest the viewport centre over an arbitrary array-order truncation.
  const center: LngLat = { lng: (bounds.minLng + bounds.maxLng) / 2, lat: (bounds.minLat + bounds.maxLat) / 2 };
  return within
    .map((s) => ({ station: s, distanceM: haversineMeters(center, s.coord) }))
    .sort((a, b) => a.distanceM - b.distanceM)
    .slice(0, maxCount)
    .map((c) => c.station);
}

/** Bounding box of the seeded road graph, padded, for the area-coverage grid. */
export function graphBounds(paddingRatio = 0.15): CoverageBounds {
  const b = ROAD_GRAPH_META.bounds;
  const padLng = (b.maxLng - b.minLng) * paddingRatio;
  const padLat = (b.maxLat - b.minLat) * paddingRatio;
  return {
    minLng: b.minLng - padLng,
    minLat: b.minLat - padLat,
    maxLng: b.maxLng + padLng,
    maxLat: b.maxLat + padLat,
  };
}

/** Centre of the seeded graph's bbox — used to prune coverage-planning candidate stations. */
export function graphCenter(): LngLat {
  const b = graphBounds(0);
  return { lng: (b.minLng + b.maxLng) / 2, lat: (b.minLat + b.maxLat) / 2 };
}

/** Grid cell size constants for viewport-driven resolution. Roughly TARGET_CELLS_ACROSS grid cells
 * across the longer axis, whatever the current zoom span — bounded so a wide zoomed-out view stays
 * cheap to compute and a tight zoomed-in view stays genuinely fine. */
const TARGET_CELLS_ACROSS = 44;
const MIN_CELL_SIZE_DEG = 0.001; // ~110 meters, even fully zoomed in
const MAX_CELL_SIZE_DEG = 0.25; // ~28 km, even zoomed out to most of NSW

/** Compute optimal grid cell size for a bounds, ensuring ~44 cells across the longer axis
 * while respecting min/max bounds for computational efficiency. */
export function cellSizeForBounds(bounds: CoverageBounds): number {
  const span = Math.max(bounds.maxLat - bounds.minLat, bounds.maxLng - bounds.minLng);
  return Math.min(MAX_CELL_SIZE_DEG, Math.max(MIN_CELL_SIZE_DEG, span / TARGET_CELLS_ACROSS));
}
